import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Trash2, Pencil, Eye, Warehouse, Users as UsersIcon } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { ExportImportBar } from '@/components/ui/ExportImportBar';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import {
  Badge, F, WMS_PAGE_SIZE, WmsDateRange, WmsPageBar, WmsSearch,
  useActiveWarehouses, useSearchPaged, useUsersLight, useWmsMerchants, useWmsViewOnly,
  wmsDateTimeIST, wmsInDateRange,
} from './WmsShared';
import { PutawayTab, InboundQcTab } from './InboundPages';
import { RtoInwardTab, ReturnsQcTab, RestockTab } from './ReturnsPages';
import { StockTransferTab, CycleCountTab, ADJUSTMENT_REASONS } from './InventoryOpsPages';
import { useWmsScope } from './WmsScope';
import { OutboundOrdersTab, OutboundDispatchTab, LabelPrintingTab } from './OutboundPages';
import { StockReportTab, SpaceReportTab, MerchantReportTab, DispatchReportTab, BillingReportTab } from './ReportsPages';

/**
 * Every WMS screen, keyed by the route segment under /wms (see WMS_NAV in AppLayout).
 * The sidebar owns navigation now — this map only decides which screen renders.
 */
export const WMS_SECTIONS: Record<string, () => JSX.Element> = {
  'dashboard': () => <Dashboard />,

  // Warehouse
  'warehouses': () => <WarehousesTab />,

  // Inventory
  'products': () => <ProductsTab />,
  'inventory': () => <Inventory />,
  'stock-transfer': () => <StockTransferTab />,
  'cycle-count': () => <CycleCountTab />,
  'stock-adjustment': () => <StockAdjustmentTab />,

  // Inbound — in process order: receive, inspect, shelve.
  'inbound/grn': () => <GrnTab />,
  'inbound/qc': () => <InboundQcTab />,
  'inbound/putaway': () => <PutawayTab />,

  // Outbound
  'outbound/orders': () => <OutboundOrdersTab />,
  'outbound/dispatch': () => <OutboundDispatchTab />,
  'outbound/labels': () => <LabelPrintingTab />,
  // The hand-keyed dispatch register still exists for goods that never became an order.
  'outbound/register': () => <DispatchTab />,

  // Returns
  'returns/rto-inward': () => <RtoInwardTab />,
  'returns/qc': () => <ReturnsQcTab />,
  'returns/restock': () => <RestockTab />,

  // Masters
  'masters/merchants': () => <AllocationsTab />,
  'masters/suppliers': () => <SuppliersTab />,

  // Reports
  'reports/stock': () => <StockReportTab />,
  'reports/space': () => <SpaceReportTab />,
  'reports/merchant': () => <MerchantReportTab />,
  'reports/dispatch': () => <DispatchReportTab />,
  'reports/ageing': () => <StockAgeing />,
  'reports/billing': () => <BillingReportTab />,
};

/** Renders the WMS screen for the given section key, falling back to the dashboard. */
export function WmsPage({ section = 'dashboard' }: { section?: string }) {
  const viewOnly = useWmsViewOnly();
  // A merchant only ever sees their own data, and only the screens that make sense read-only.
  const allowed = viewOnly ? MERCHANT_SECTIONS : null;
  const key = (allowed && !allowed.has(section)) ? 'dashboard' : section;
  const render = WMS_SECTIONS[key] ?? WMS_SECTIONS['dashboard'];
  return <div className="space-y-4">{render()}</div>;
}

const MERCHANT_SECTIONS = new Set([
  'dashboard', 'inventory', 'products', 'inbound/grn', 'outbound/register',
  'returns/rto-inward', 'reports/ageing', 'reports/stock',
]);

// ───────── DASHBOARD ─────────
function Dashboard() {
  const viewOnly = useWmsViewOnly();
  const { data: stock } = useQuery({ queryKey: ['wms-stock-all'], queryFn: async () => (await api.get('/wms/inventory/stock', { params: { } })).data as any[] });
  const { data: grns } = useQuery({ queryKey: ['wms-grns-recent'], queryFn: async () => (await api.get('/wms/grn', { params: { limit: 5 } })).data as any[] });

  const totals = useMemo(() => {
    const items = stock ?? [];
    const available = items.reduce((s: number, r: any) => s + (r.available ?? 0), 0);
    const damaged = items.reduce((s: number, r: any) => s + (r.damaged ?? 0), 0);
    const skus = new Set(items.map((r: any) => r.productId)).size;
    const merchants = new Set(items.map((r: any) => r.merchantId)).size;
    return { available, damaged, skus, merchants };
  }, [stock]);

  return (
    <div className="space-y-4">
      <div className={`grid ${viewOnly ? 'grid-cols-3' : 'grid-cols-4'} gap-3`}>
        <Stat label="Total Units in Stock" value={totals.available.toLocaleString()} accent="emerald" />
        <Stat label="Damaged Units" value={totals.damaged.toLocaleString()} accent="amber" />
        <Stat label="Unique SKUs" value={String(totals.skus)} />
        {!viewOnly && <Stat label="Merchants Stored" value={String(totals.merchants)} accent="blue" />}
      </div>
      <StockSummary />
      <div className="bg-white border rounded-xl p-4">
        <h3 className="font-semibold mb-3">Recent Inward (GRN)</h3>
        <table className="min-w-full text-sm">
          <thead className="text-xs text-slate-500"><tr><th className="text-left p-2">GRN No</th><th className="text-left">Supplier</th><th className="text-right">Qty</th><th className="text-right">Value</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            {(grns ?? []).map((g: any) => (
              <tr key={g.id} className="border-t">
                <td className="p-2 font-mono text-xs">{g.grnNo}</td>
                <td>{g.supplier?.name ?? g.supplierName ?? '—'}</td>
                <td className="text-right">{g.totalQty}</td>
                <td className="text-right">₹{Number(g.totalValue).toFixed(2)}</td>
                <td><Badge text={g.status} /></td>
                <td className="text-xs text-slate-500">{g.createdAt?.slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Per-SKU stock movement overview: Opening → Inbound / Dispatch / Returns → Current, with alerts.
// Date range filters the movement buckets and computes Current "as of" the To date (server-side).
function StockSummary() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [alertFilter, setAlertFilter] = useState('');
  const { data = [], isLoading } = useQuery({
    queryKey: ['wms-stock-summary', from, to],
    queryFn: async () => (await api.get('/wms/inventory/stock-summary', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } })).data as any[],
  });
  const alertFiltered = (data as any[]).filter((r) => !alertFilter || r.alert === alertFilter);
  const { q, setQ, page, setPage, total, offset, pageRows } = useSearchPaged(
    alertFiltered,
    (r, t) => [r.sku, r.name].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const alertBadge = (a: string) => {
    const map: Record<string, { txt: string; cls: string }> = {
      OUT_OF_STOCK: { txt: 'Out of Stock', cls: 'bg-red-100 text-red-700' },
      LOW_STOCK: { txt: 'Low Stock', cls: 'bg-amber-100 text-amber-700' },
      IN_STOCK: { txt: 'In Stock', cls: 'bg-emerald-100 text-emerald-700' },
    };
    const m = map[a] ?? map.IN_STOCK;
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${m.cls}`}>{m.txt}</span>;
  };
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="font-semibold">Stock Summary</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <WmsSearch value={q} onChange={setQ} placeholder="Search SKU / name" className="w-56" />
          <select value={alertFilter} onChange={(e) => { setAlertFilter(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm">
            <option value="">All alerts</option>
            <option value="IN_STOCK">In Stock</option>
            <option value="LOW_STOCK">Low Stock</option>
            <option value="OUT_OF_STOCK">Out of Stock</option>
          </select>
          <WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="As of / range" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">S.No</th><th className="text-left">SKU Name</th><th className="text-left">SKU Code</th>
            <th className="text-right">Opening</th><th className="text-right">Inbound</th><th className="text-right">Dispatch</th>
            <th className="text-right">Returns</th><th className="text-right">Current</th><th className="text-center">Alert</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : total === 0 ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">{q || alertFilter || from || to ? 'No matching SKUs' : 'No stock activity yet'}</td></tr>
              : pageRows.map((r: any, i: number) => (
                <tr key={r.productId} className="border-t">
                  <td className="p-2 text-slate-500">{offset + i + 1}</td>
                  <td>{r.name || '—'}</td>
                  <td className="font-mono text-xs">{r.sku || '—'}</td>
                  <td className="text-right">{r.opening}</td>
                  <td className="text-right text-emerald-700">{r.inbound}</td>
                  <td className="text-right text-red-600">{r.dispatch}</td>
                  <td className="text-right text-blue-700">{r.returns}</td>
                  <td className="text-right font-semibold">{r.current}</td>
                  <td className="text-center">{alertBadge(r.alert)}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
    </div>
  );
}

function Stat({ label, value, accent }: any) {
  const c = accent === 'emerald' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : accent === 'amber' ? 'border-amber-200 bg-amber-50 text-amber-700' : accent === 'blue' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-slate-50 text-slate-700';
  return <div className={`p-3 rounded border ${c}`}><div className="text-xs uppercase">{label}</div><div className="text-2xl font-bold">{value}</div></div>;
}

// ───────── INVENTORY ─────────
function Inventory() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [adjust, setAdjust] = useState(false);
  const [bulkAdjust, setBulkAdjust] = useState(false);
  const [view, setView] = useState<'stock' | 'history' | 'ageing'>('stock');
  const { data, isLoading } = useQuery({ queryKey: ['wms-stock', q], queryFn: async () => (await api.get('/wms/inventory/stock', { params: q ? { q } : {} })).data as any[] });
  // Date range applies to when the stock line was last updated (received / adjusted).
  const rows = (data ?? []).filter((r: any) => wmsInDateRange(r.lastUpdatedAt, from, to));
  const total = rows.length;
  const lastPage = Math.max(1, Math.ceil(total / WMS_PAGE_SIZE));
  const safePage = Math.min(page, lastPage);
  const pageRows = rows.slice((safePage - 1) * WMS_PAGE_SIZE, safePage * WMS_PAGE_SIZE);
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex gap-2 mb-3">
        <button onClick={() => setView('stock')} className={`px-3 py-1.5 rounded text-sm font-medium ${view === 'stock' ? 'bg-emerald-600 text-white' : 'border text-slate-600'}`}>Stock Levels</button>
        <button onClick={() => setView('history')} className={`px-3 py-1.5 rounded text-sm font-medium ${view === 'history' ? 'bg-emerald-600 text-white' : 'border text-slate-600'}`}>Movement History</button>
        <button onClick={() => setView('ageing')} className={`px-3 py-1.5 rounded text-sm font-medium ${view === 'ageing' ? 'bg-emerald-600 text-white' : 'border text-slate-600'}`}>Stock Ageing</button>
      </div>
      {view === 'ageing' ? <StockAgeing /> : view === 'history' ? <StockHistory /> : (<>
      <div className="flex gap-2 mb-3 items-center">
        <WmsSearch value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search SKU / name" className="flex-1" />
        <WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="Updated" />
        {!viewOnly && <ExportImportBar resource="wms/inventory-opening" onImported={() => qc.invalidateQueries({ queryKey: ['wms-stock'] })} />}
        {!viewOnly && <button onClick={() => setAdjust(true)} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1 whitespace-nowrap"><Plus size={14} /> Opening / Physical Stock</button>}
        {!viewOnly && <button onClick={() => setBulkAdjust(true)} className="border border-emerald-600 text-emerald-700 px-3 py-1.5 rounded text-sm flex items-center gap-1 whitespace-nowrap"><Plus size={14} /> Bulk Adjustment</button>}
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-emerald-50"><tr>
          <th className="p-2 text-left">SKU</th><th className="p-2 text-left">Product</th>
          {!viewOnly && <th className="text-left">Merchant</th>}<th className="text-left">Warehouse</th><th className="text-left">Bin</th>
          {/* Only Available is sellable — the other three are physically here but held back. */}
          <th className="text-right">Available</th><th className="text-right">Damaged</th>
          <th className="text-right">QC Hold</th><th className="text-right">Rejected</th>
        </tr></thead>
        <tbody>
          {isLoading ? <tr><td colSpan={viewOnly ? 9 : 10} className="text-center py-6 text-slate-400">Loading…</td></tr>
            : total === 0 ? <tr><td colSpan={viewOnly ? 9 : 10} className="text-center py-6 text-slate-400">No stock found</td></tr>
            : pageRows.map((r: any) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 font-mono text-xs">{r.product?.sku}</td>
                <td>{r.product?.name}</td>
                {!viewOnly && <td>M#{r.merchantId}</td>}
                <td>W#{r.warehouseId}</td>
                <td className="font-mono text-xs">{r.bin ?? '—'}</td>
                <td className="text-right font-medium text-emerald-700">{r.available}</td>
                <td className="text-right text-red-600">{r.damaged}</td>
                <td className="text-right text-amber-600">{r.qcHold || '—'}</td>
                <td className="text-right text-slate-500">{r.rejected || '—'}</td>
              </tr>
            ))
          }
        </tbody>
      </table>
      <WmsPageBar page={safePage} total={total} onPage={setPage} />
      </>)}
      {adjust && <StockAdjustForm onClose={() => setAdjust(false)} onSaved={() => { setAdjust(false); qc.invalidateQueries({ queryKey: ['wms-stock'] }); }} />}
      {bulkAdjust && <BulkStockAdjustForm onClose={() => setBulkAdjust(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['wms-stock'] })} />}
    </div>
  );
}

// ───────── STOCK AGEING ─────────
// How long the stock we're STILL holding has been sitting in the warehouse. The backend replays the
// inventory ledger with strict FIFO (oldest lot consumed first), so what's left is real on-hand stock
// with a real received-date, then buckets it by age. Oldest stock is listed first — that's what ops
// needs to clear. Click a row to see its individual lots.
function StockAgeing() {
  const viewOnly = useWmsViewOnly();
  const { data: merchants } = useWmsMerchants();
  const { data: warehouses } = useActiveWarehouses();
  const [merchantId, setMerchantId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [asOf, setAsOf] = useState('');
  const [bucket, setBucket] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const params = {
    ...(merchantId ? { merchantId } : {}),
    ...(warehouseId ? { warehouseId } : {}),
    ...(asOf ? { asOf } : {}),
    ...(bucket ? { bucket } : {}),
  };
  const { data, isLoading } = useQuery({
    queryKey: ['wms-ageing', merchantId, warehouseId, asOf, bucket],
    queryFn: async () => (await api.get('/wms/inventory/ageing', { params })).data as {
      asOf: string; bucketLabels: string[];
      totals: { qty: number; value: number; skus: number; buckets: number[]; bucketValues: number[] };
      items: any[];
    },
  });
  const labels = data?.bucketLabels ?? [];
  const { q, setQ, page, setPage, total, offset, pageRows } = useSearchPaged(
    data?.items ?? [],
    (r: any, t) => [r.sku, r.name].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );

  // Older = hotter. Last bucket (dead stock) is red, first is green.
  const bucketTone = (i: number) => {
    const tones = ['text-emerald-700', 'text-lime-700', 'text-amber-700', 'text-orange-700', 'text-red-700', 'text-red-800'];
    return tones[Math.min(i, tones.length - 1)];
  };
  const bucketBg = (i: number) => {
    const tones = ['border-emerald-200 bg-emerald-50', 'border-lime-200 bg-lime-50', 'border-amber-200 bg-amber-50', 'border-orange-200 bg-orange-50', 'border-red-200 bg-red-50', 'border-red-300 bg-red-100'];
    return tones[Math.min(i, tones.length - 1)];
  };
  const money = (v: number) => '₹' + Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  const exportCsv = () => {
    const qs = new URLSearchParams(params as any).toString();
    downloadBlob(`/wms/inventory/ageing/export${qs ? `?${qs}` : ''}`, `wms-inventory-ageing-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div>
      <div className="flex gap-2 mb-3 items-center flex-wrap">
        <WmsSearch value={q} onChange={setQ} placeholder="Search SKU / product" className="w-64" />
        {!viewOnly && (
          <select value={merchantId} onChange={(e) => { setMerchantId(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm">
            <option value="">All merchants</option>
            {(merchants ?? []).map((m: any) => <option key={m.id} value={m.id}>{m.companyName}</option>)}
          </select>
        )}
        <select value={warehouseId} onChange={(e) => { setWarehouseId(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm">
          <option value="">All warehouses</option>
          {(warehouses ?? []).map((w: any) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={bucket} onChange={(e) => { setBucket(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm">
          <option value="">All age buckets</option>
          {labels.map((l, i) => <option key={l} value={String(i)}>Holding stock {l}</option>)}
        </select>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500 whitespace-nowrap">As of</span>
          <input type="date" value={asOf} onChange={(e) => { setAsOf(e.target.value); setPage(1); }} title="Age the stock as it stood on this date" className="border rounded px-2 py-1.5 text-sm" />
          {asOf && <button onClick={() => setAsOf('')} title="Back to today" className="text-xs text-slate-400 hover:text-slate-700">✕</button>}
        </div>
        <button onClick={exportCsv} className="ml-auto border border-slate-300 px-3 py-1.5 rounded text-sm hover:bg-slate-50 whitespace-nowrap">Export CSV</button>
      </div>

      {/* Bucket summary — units + stock value per age band */}
      <div className={`grid gap-2 mb-3`} style={{ gridTemplateColumns: `repeat(${Math.max(labels.length + 1, 2)}, minmax(0, 1fr))` }}>
        <div className="p-2.5 rounded border border-slate-200 bg-slate-50">
          <div className="text-[11px] uppercase text-slate-500">Total on hand</div>
          <div className="text-xl font-bold text-slate-800">{(data?.totals.qty ?? 0).toLocaleString()}</div>
          <div className="text-[11px] text-slate-500">{money(data?.totals.value ?? 0)} · {data?.totals.skus ?? 0} SKUs</div>
        </div>
        {labels.map((l, i) => (
          <button key={l} onClick={() => { setBucket(bucket === String(i) ? '' : String(i)); setPage(1); }}
            title={`Show only SKUs holding stock ${l}`}
            className={`text-left p-2.5 rounded border transition ${bucketBg(i)} ${bucket === String(i) ? 'ring-2 ring-emerald-500' : 'hover:brightness-95'}`}>
            <div className="text-[11px] uppercase text-slate-600">{l}</div>
            <div className={`text-xl font-bold ${bucketTone(i)}`}>{(data?.totals.buckets[i] ?? 0).toLocaleString()}</div>
            <div className="text-[11px] text-slate-500">{money(data?.totals.bucketValues[i] ?? 0)}</div>
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">S.No</th><th className="text-left">SKU Code</th><th className="text-left">Product</th>
            {!viewOnly && <th className="text-left">Merchant</th>}<th className="text-left">WH</th>
            {labels.map((l, i) => <th key={l} className={`text-right ${bucketTone(i)}`}>{l}</th>)}
            <th className="text-right">On hand</th><th className="text-right">Value</th>
            <th className="text-right">Oldest</th><th className="text-right">Avg age</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={labels.length + 9} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : total === 0 ? <tr><td colSpan={labels.length + 9} className="text-center py-6 text-slate-400">
                  {q || bucket || merchantId || warehouseId ? 'No stock matches these filters' : 'No stock on hand to age yet'}
                </td></tr>
              : pageRows.map((r: any, i: number) => {
                const key = `${r.productId}-${r.merchantId}-${r.warehouseId}`;
                const isOpen = expanded === key;
                return (
                  <Fragment key={key}>
                    <tr onClick={() => setExpanded(isOpen ? null : key)}
                      className="border-t cursor-pointer hover:bg-slate-50" title="Click to see the individual lots behind these numbers">
                      <td className="p-2 text-slate-500">{offset + i + 1}</td>
                      <td className="font-mono text-xs">{r.sku || '—'}</td>
                      <td className="text-xs">{r.name || '—'}</td>
                      {!viewOnly && <td className="text-xs">M#{r.merchantId}</td>}
                      <td className="text-xs">W#{r.warehouseId}</td>
                      {labels.map((l, bi) => (
                        <td key={l} className={`text-right ${r.buckets[bi] ? bucketTone(bi) + ' font-medium' : 'text-slate-300'}`}>
                          {r.buckets[bi] ? r.buckets[bi].toLocaleString() : '—'}
                        </td>
                      ))}
                      <td className="text-right font-semibold">{r.totalQty.toLocaleString()}</td>
                      <td className="text-right text-slate-600">{money(r.totalValue)}</td>
                      <td className="text-right text-xs text-slate-500 whitespace-nowrap">{r.maxAgeDays}d</td>
                      <td className="text-right text-xs text-slate-500">{r.avgAgeDays}d</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50 border-t">
                        <td colSpan={labels.length + 9} className="px-4 py-3">
                          <div className="text-xs font-medium text-slate-600 mb-1.5">Lots still on hand — oldest first</div>
                          <table className="text-xs">
                            <thead className="text-slate-500"><tr>
                              <th className="text-left pr-6 pb-1">Received</th><th className="text-left pr-6 pb-1">Age</th>
                              <th className="text-right pr-6 pb-1">Qty</th><th className="text-left pr-6 pb-1">Bin</th>
                              <th className="text-left pr-6 pb-1">Batch</th><th className="text-right pb-1">Unit cost</th>
                            </tr></thead>
                            <tbody>
                              {r.lots.map((lot: any, li: number) => (
                                <tr key={li} className="border-t border-slate-200">
                                  <td className="pr-6 py-1 whitespace-nowrap">{wmsDateTimeIST(lot.receivedAt)}</td>
                                  <td className="pr-6 py-1"><span className="px-1.5 py-0.5 rounded bg-white border text-[10px]">{lot.ageDays}d · {lot.bucket}</span></td>
                                  <td className="pr-6 py-1 text-right font-medium">{lot.qty.toLocaleString()}</td>
                                  <td className="pr-6 py-1 font-mono">{lot.bin ?? '—'}</td>
                                  <td className="pr-6 py-1 font-mono">{lot.batchNo ?? '—'}</td>
                                  <td className="py-1 text-right">{lot.unitCost != null ? money(lot.unitCost) : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            }
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      <p className="text-[11px] text-slate-400 mt-2">
        Ageing is FIFO: the oldest received units are treated as dispatched first, so the remaining lots carry
        their true received dates. Damaged stock is excluded (it never sits in available stock).
        {data?.asOf ? ` Aged as of ${new Date(data.asOf).toLocaleDateString('en-IN')}.` : ''}
      </p>
    </div>
  );
}

// Stock movement history — every Inward / Outward / RTO / Adjustment / Damage line with reason + who did it.
function StockHistory() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [type, setType] = useState('');
  const { data = [], isLoading } = useQuery({
    queryKey: ['wms-ledger', type],
    queryFn: async () => (await api.get('/wms/inventory/ledger', { params: { limit: 1000, ...(type ? { type } : {}) } })).data as any[],
  });
  const dateFiltered = (data as any[]).filter((r) => wmsInDateRange(r.createdAt, from, to));
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    dateFiltered,
    (r, t) => [r.product?.sku, r.product?.name, r.type, r.notes, r.awbNo, r.invoiceNumber, r.customerName, r.createdByName, r.bin].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const TYPES = ['INWARD', 'OUTWARD', 'RTO_INWARD', 'ADJUSTMENT', 'DAMAGE', 'TRANSFER'];
  return (
    <div>
      <div className="flex gap-2 mb-3 items-center flex-wrap">
        <WmsSearch value={q} onChange={setQ} placeholder="Search SKU / reason / AWB / user" className="w-72" />
        <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm"><option value="">All movements</option>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="Date" />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">Date</th><th className="text-left">SKU</th><th className="text-left">Product</th>
            <th className="text-left">Movement</th><th className="text-right">Qty</th><th className="text-left">Bin</th>
            <th className="text-left">Reference</th><th className="text-left">Reason</th><th className="text-left">By (User)</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : total === 0 ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">{q || from || to || type ? 'No matching movements' : 'No stock movements yet'}</td></tr>
              : pageRows.map((r: any) => (
                <tr key={r.id} className="border-t">
                  <td className="p-2 text-xs text-slate-500 whitespace-nowrap">{wmsDateTimeIST(r.createdAt)}</td>
                  <td className="font-mono text-xs">{r.product?.sku ?? '—'}</td>
                  <td className="text-xs">{r.product?.name ?? '—'}</td>
                  <td><Badge text={r.type} /></td>
                  <td className={`text-right font-medium ${Number(r.qty) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{Number(r.qty) > 0 ? '+' : ''}{r.qty}</td>
                  <td className="font-mono text-xs">{r.bin ?? '—'}</td>
                  <td className="text-xs text-slate-500">{r.referenceType ?? '—'}{r.awbNo ? ` · ${r.awbNo}` : ''}{r.invoiceNumber ? ` · ${r.invoiceNumber}` : ''}</td>
                  <td className="text-xs text-slate-600">{r.notes || r.referenceType || r.type || '—'}{r.customerName ? ` (${r.customerName})` : ''}</td>
                  <td className="text-xs">{r.createdByName ?? (r.createdById ? `User #${r.createdById}` : '—')}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
    </div>
  );
}

/**
 * Stock Adjustment screen — make an opening / physical correction, and see every adjustment made.
 * The history is the inventory ledger filtered to ADJUSTMENT rows, so it shows exactly what moved,
 * why, and who did it.
 */
function StockAdjustmentTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [show, setShow] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data = [] } = useQuery({
    queryKey: ['wms-adjustments'],
    queryFn: async () => (await api.get('/wms/inventory/ledger', { params: { type: 'ADJUSTMENT', limit: 1000 } })).data as any[],
  });
  const { data: merchants = [] } = useWmsMerchants();
  const mName = (id: any) => (merchants as any[]).find((m) => m.id === id)?.companyName ?? (id ? `M#${id}` : '—');

  const rows = (data as any[]).filter((r) => wmsInDateRange(r.createdAt, from, to));
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    rows,
    (r: any, t) => [r.product?.sku, r.product?.name, r.notes, r.referenceType, r.createdByName, mName(r.merchantId)]
      .some((v) => String(v ?? '').toLowerCase().includes(t)),
  );

  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-semibold">Stock Adjustment</h3>
          <WmsSearch value={q} onChange={setQ} placeholder="Search SKU / reason / user" className="w-72" />
          <WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="Adjusted" />
        </div>
        {!viewOnly && <button onClick={() => setShow(true)} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> Adjust Stock</button>}
      </div>
      <p className="text-xs text-slate-500 mb-3">Use this for opening stock and physical corrections. Routine counts are better done through Cycle Count, which records the variance against a count sheet.</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">When</th>{!viewOnly && <th className="text-left">Merchant</th>}
            <th className="text-left">SKU</th><th className="text-left">Product</th><th className="text-left">Bin</th>
            <th className="text-right">Change</th><th className="text-left">Source</th><th className="text-left">Reason</th><th className="text-left">By</th>
          </tr></thead>
          <tbody>
            {total === 0
              ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">{q || from || to ? 'No matching adjustments' : 'No stock adjustments yet'}</td></tr>
              : pageRows.map((r: any) => (
                <tr key={r.id} className="border-t">
                  <td className="p-2 text-xs text-slate-500">{wmsDateTimeIST(r.createdAt)}</td>
                  {!viewOnly && <td className="text-xs">{mName(r.merchantId)}</td>}
                  <td className="font-mono text-xs">{r.product?.sku ?? '—'}</td>
                  <td className="text-xs">{r.product?.name ?? '—'}</td>
                  <td className="font-mono text-xs">{r.bin ?? '—'}</td>
                  <td className={`text-right font-medium ${Number(r.qty) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{Number(r.qty) > 0 ? '+' : ''}{r.qty}</td>
                  <td className="text-xs">{r.referenceType ?? '—'}</td>
                  <td className="text-xs text-slate-500">{r.notes ?? '—'}</td>
                  <td className="text-xs">{r.createdByName ?? '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && <StockAdjustForm onClose={() => setShow(false)} onSaved={() => {
        setShow(false);
        qc.invalidateQueries({ queryKey: ['wms-adjustments'] });
        qc.invalidateQueries({ queryKey: ['wms-stock'] });
      }} />}
    </div>
  );
}

/**
 * BULK stock adjustment — correct many SKUs in one pass.
 *
 * One reason and one remark cover the whole batch (that is the point: "stocktake 12 Aug", "water
 * damage in bay 3"), and each line names a SKU and the counted or delta quantity. Every row is
 * applied independently and reported back with its own outcome, so one bad SKU never abandons the
 * rest. A CSV of the same shape can be uploaded instead via the Import button on this screen.
 */
function BulkStockAdjustForm({ onClose, onSaved }: any) {
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  const { data: prodData } = useQuery({ queryKey: ['wms-products-all'], queryFn: async () => (await api.get('/wms/products', { params: { pageSize: 100000 } })).data as any });
  const products = prodData?.items ?? [];
  const options = (products as any[]).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}`, hint: p.merchantId ? `M#${p.merchantId}` : '' }));

  // Start from the warehouse/merchant already open on screen — retyping what the breadcrumb
  // already says is a step that only exists to be got wrong.
  const scope = useWmsScope();
  const [hdr, setHdr] = useState<any>({ mode: 'SET', merchantId: scope.merchantId ?? '', warehouseId: scope.warehouseId ?? '' });
  const [rows, setRows] = useState<any[]>([{ productId: '', qty: '', bin: '', batchNo: '' }]);
  const [result, setResult] = useState<any>(null);
  const setRow = (i: number, k: string, v: any) => setRows((a) => a.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addRow = () => setRows((a) => [...a, { productId: '', qty: '', bin: '', batchNo: '' }]);
  const rmRow = (i: number) => setRows((a) => a.filter((_, idx) => idx !== i));

  const filled = rows.filter((r) => r.productId && r.qty !== '' && Number.isFinite(Number(r.qty)));
  const canSave = !!hdr.merchantId && !!hdr.warehouseId && !!hdr.reason && !!String(hdr.notes ?? '').trim() && filled.length > 0;

  const save = useMutation({
    mutationFn: async () => (await api.post('/wms/inventory/bulk-adjust', {
      merchantId: Number(hdr.merchantId), warehouseId: Number(hdr.warehouseId),
      mode: hdr.mode, reason: hdr.reason, notes: hdr.notes,
      rows: filled.map((r) => ({
        productId: r.productId, qty: Number(r.qty),
        // Send the SKU so the per-row result reads "SKU-001" rather than an internal product id.
        sku: (products as any[]).find((p) => String(p.id) === String(r.productId))?.sku,
        bin: String(r.bin ?? '').trim() || null, batchNo: String(r.batchNo ?? '').trim() || null,
        notes: String(r.notes ?? '').trim() || undefined,
      })),
    })).data,
    onSuccess: (r: any) => { setResult(r); onSaved(); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not apply the adjustments'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[980px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold">Bulk Stock Adjustment</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs bg-amber-50 text-amber-800 rounded px-3 py-2">
            Every line is written to the stock ledger with the reason and remarks below, so the change can be explained later. Rows are applied one by one — a line that fails does not stop the others.
          </div>
          <div className="grid grid-cols-4 gap-3">
            <F label="Merchant *"><select value={hdr.merchantId ?? ''} onChange={(e) => setHdr({ ...hdr, merchantId: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
            <F label="Warehouse *"><select value={hdr.warehouseId ?? ''} onChange={(e) => setHdr({ ...hdr, warehouseId: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
            <F label="Update type"><select value={hdr.mode} onChange={(e) => setHdr({ ...hdr, mode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="SET">Set counted quantity</option><option value="DELTA">Adjust by (+ / −)</option></select></F>
            <F label="Reason *"><select value={hdr.reason ?? ''} onChange={(e) => setHdr({ ...hdr, reason: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">— select —</option>{ADJUSTMENT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></F>
            <div className="col-span-4"><F label="Remarks *"><input value={hdr.notes ?? ''} onChange={(e) => setHdr({ ...hdr, notes: e.target.value })} placeholder="What was found, and why the stock differs" className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Lines</div>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                <div className="col-span-5"><F label="Product *"><SearchableSelect value={r.productId || ''} onChange={(v) => setRow(i, 'productId', v)} options={options} placeholder="Search SKU / name…" /></F></div>
                <div className="col-span-2"><F label={hdr.mode === 'DELTA' ? 'Change (+/−) *' : 'Counted qty *'}><input type="number" value={r.qty} onChange={(e) => setRow(i, 'qty', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
                <div className="col-span-2"><F label="Bin"><input value={r.bin ?? ''} onChange={(e) => setRow(i, 'bin', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F></div>
                <div className="col-span-2"><F label="Batch"><input value={r.batchNo ?? ''} onChange={(e) => setRow(i, 'batchNo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F></div>
                <div className="col-span-1">{rows.length > 1 && <button onClick={() => rmRow(i)} className="border border-red-300 text-red-600 p-1.5 rounded"><Trash2 size={14} /></button>}</div>
              </div>
            ))}
            <button onClick={addRow} className="text-emerald-700 border border-emerald-300 px-3 py-1 rounded text-xs flex items-center gap-1"><Plus size={12} /> Add line</button>
          </div>

          {result && (
            <div className="border rounded">
              <div className="px-3 py-2 text-sm bg-slate-50 border-b">
                <span className="text-emerald-700 font-medium">{result.adjusted} adjusted</span>
                {result.failed > 0 && <span className="text-red-600 font-medium ml-3">{result.failed} failed</span>}
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="min-w-full text-xs">
                  <tbody>
                    {(result.results ?? []).map((r: any) => (
                      <tr key={r.row} className="border-t">
                        <td className="p-2 w-12 text-slate-400">#{r.row}</td>
                        <td className="font-mono">{r.ref}</td>
                        <td className={r.status === 'error' ? 'text-red-600' : 'text-emerald-700'}>{r.status}</td>
                        <td className="text-slate-600">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border px-4 py-1.5 rounded text-sm">Close</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !canSave}
            title={!canSave ? 'Pick a merchant, warehouse, reason and remarks, and fill at least one line' : undefined}
            className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
            {save.isPending ? 'Applying…' : `Apply ${filled.length || ''} Adjustment${filled.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Opening / Physical stock update. SET = enter the counted (absolute) qty; DELTA = enter a +/- change.
function StockAdjustForm({ onClose, onSaved }: any) {
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  const { data: prodData } = useQuery({ queryKey: ['wms-products-all'], queryFn: async () => (await api.get('/wms/products', { params: { pageSize: 100000 } })).data as any });
  const products = prodData?.items ?? [];
  const productOptions = (products as any[]).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}`, hint: p.merchantId ? `M#${p.merchantId}` : '' }));
  const [form, setForm] = useState<any>({ mode: 'SET', kind: 'OPENING' });
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const bin = (form.bin ?? '').trim() || null;
  const batchNo = (form.batchNo ?? '').trim() || null;
  const canLookup = !!(form.productId && form.merchantId && form.warehouseId);
  // Current stock for the chosen product+merchant+warehouse (to show what's on hand before the change).
  const { data: stockRows } = useQuery({
    queryKey: ['wms-stock-lookup', form.productId, form.merchantId, form.warehouseId],
    enabled: canLookup,
    queryFn: async () => (await api.get('/wms/inventory/stock', { params: { productId: form.productId, merchantId: form.merchantId, warehouseId: form.warehouseId } })).data as any[],
  });
  const currentRow = (stockRows ?? []).find((r: any) => ((r.bin ?? null) === bin) && ((r.batchNo ?? null) === batchNo));
  const current = currentRow?.available ?? 0;

  const qtyNum = Number(form.qty);
  const qtyValid = form.qty !== undefined && form.qty !== '' && Number.isFinite(qtyNum);
  const badNum = Number(form.badQty);
  const badValid = form.badQty !== undefined && form.badQty !== '' && Number.isFinite(badNum) && badNum >= 0;
  const newAvailable = form.mode === 'DELTA' ? current + (qtyValid ? qtyNum : 0) : (qtyValid ? qtyNum : current);
  // An OPENING load is a stated starting position. A CYCLE_COUNT adjustment rewrites stock the system
  // already believed in, so it has to name a reason and be described in words.
  const isAdjustment = form.kind === 'CYCLE_COUNT';
  const justified = !isAdjustment || (!!form.reason && !!String(form.notes ?? '').trim());
  // Save if a Good and/or Bad quantity is provided.
  const canSave = canLookup && justified && (qtyValid || (badValid && badNum > 0)) && (!qtyValid || form.mode !== 'SET' || qtyNum >= 0) && (!qtyValid || newAvailable >= 0);

  const save = useMutation({
    mutationFn: async () => (await api.post('/wms/inventory/adjust', {
      productId: form.productId, merchantId: Number(form.merchantId), warehouseId: Number(form.warehouseId),
      bin, batchNo, mode: form.mode, kind: form.kind,
      qty: qtyValid ? qtyNum : undefined,
      badQty: badValid ? badNum : undefined,
      unitCost: form.unitCost, reason: form.reason, notes: form.notes,
    })).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not update the stock'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[680px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">Opening / Physical Stock Update</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <F label="Merchant *"><select value={form.merchantId ?? ''} onChange={(e) => set('merchantId', e.target.value ? Number(e.target.value) : null)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
          <F label="Warehouse * (active)"><select value={form.warehouseId ?? ''} onChange={(e) => set('warehouseId', e.target.value ? Number(e.target.value) : null)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
          <div className="col-span-2"><F label="Product *"><SearchableSelect value={form.productId || ''} onChange={(v) => set('productId', v)} options={productOptions} placeholder="Search SKU / name…" /></F></div>
          <F label="Bin (optional)"><input value={form.bin ?? ''} onChange={(e) => set('bin', e.target.value)} placeholder="A-01" className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="Batch (optional)"><input value={form.batchNo ?? ''} onChange={(e) => set('batchNo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="Update type"><select value={form.mode} onChange={(e) => set('mode', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="SET">Set counted quantity (physical/opening)</option><option value="DELTA">Adjust by (+ / −)</option></select></F>
          <F label="Entry type"><select value={form.kind} onChange={(e) => set('kind', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="OPENING">Opening stock</option><option value="CYCLE_COUNT">Physical count / stock adjustment</option></select></F>
          {isAdjustment && (
            <F label="Reason *">
              <select value={form.reason ?? ''} onChange={(e) => set('reason', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">— select —</option>
                {ADJUSTMENT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </F>
          )}
          <F label={form.mode === 'DELTA' ? 'Good / Available qty (+/−)' : 'Good / Available qty'}><input type="number" value={form.qty ?? ''} onChange={(e) => set('qty', e.target.value)} placeholder="0" className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Bad / Damaged qty (adds to damaged)"><input type="number" value={form.badQty ?? ''} onChange={(e) => set('badQty', e.target.value)} placeholder="0" className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Unit Cost (optional)"><input type="number" step="0.01" value={form.unitCost ?? ''} onChange={(e) => set('unitCost', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <div className="col-span-2">
            <F label={isAdjustment ? 'Remarks *' : 'Notes (optional)'}>
              <input value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)}
                placeholder={isAdjustment ? 'What was found, and why the stock differs' : ''}
                className={`w-full border rounded px-2 py-1.5 text-sm ${isAdjustment && !String(form.notes ?? '').trim() ? 'border-rose-400 bg-rose-50' : ''}`} />
            </F>
          </div>
          {canLookup && (
            <div className="col-span-2 flex items-center gap-4 text-sm bg-slate-50 border rounded px-3 py-2">
              <span className="text-slate-500">Current available: <span className="font-semibold text-slate-800">{current}</span></span>
              <span className="text-slate-400">→</span>
              <span className="text-slate-500">New available: <span className={`font-semibold ${newAvailable < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{Number.isFinite(newAvailable) ? newAvailable : current}</span></span>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3"><button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button><button onClick={() => save.mutate()} disabled={save.isPending || !canSave} title={!canSave ? (canLookup && !justified ? 'A stock adjustment needs a reason and remarks' : 'Pick merchant, warehouse, product and a valid quantity') : undefined} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Update Stock'}</button></div>
      </div>
    </div>
  );
}

// ───────── PRODUCTS ─────────
function ProductsTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  // Products are paginated server-side (products.service returns { total, page, pageSize, items }).
  const { data } = useQuery({ queryKey: ['wms-products', q, page], queryFn: async () => (await api.get('/wms/products', { params: { ...(q ? { q } : {}), page, pageSize: WMS_PAGE_SIZE } })).data as any });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex justify-between mb-3">
        <WmsSearch value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search SKU / name / barcode" className="w-72" />
        <div className="flex items-center gap-2">
          {!viewOnly && <ExportImportBar resource="wms/products" onImported={() => qc.invalidateQueries({ queryKey: ['wms-products'] })} />}
          {!viewOnly && <button onClick={() => { setEditing(null); setShow(true); }} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> Add Product</button>}
        </div>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-emerald-50"><tr>
          <th className="p-2 text-left">ID</th><th className="text-left">SKU Code</th><th className="text-left">SKU Name</th>
          <th className="text-left">Barcode</th><th className="text-right">MRP</th><th className="text-left">HSN</th>
          <th className="text-right">Wt</th>{!viewOnly && <th></th>}
        </tr></thead>
        <tbody>
          {items.length === 0 ? <tr><td colSpan={viewOnly ? 7 : 8} className="text-center py-6 text-slate-400">No products yet</td></tr>
            : items.map((p: any) => (
              <tr key={p.id} className="border-t">
                <td className="p-2 font-mono text-xs text-slate-500">{p.id}</td>
                <td className="font-mono text-xs">{p.sku}</td>
                <td>{p.name}</td>
                <td className="font-mono text-xs">{p.barcode ?? '—'}</td>
                <td className="text-right">{p.mrp ? '₹'+Number(p.mrp).toFixed(2) : '—'}</td>
                <td className="font-mono text-xs">{p.hsnCode ?? '—'}</td>
                <td className="text-right">{p.weight ? `${p.weight}kg` : '—'}</td>
                {!viewOnly && <td><button onClick={() => { setEditing(p); setShow(true); }} className="text-emerald-700"><Pencil size={14} /></button></td>}
              </tr>
            ))
          }
        </tbody>
      </table>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && <ProductForm initial={editing} onClose={() => setShow(false)} onSaved={() => { setShow(false); qc.invalidateQueries({ queryKey: ['wms-products'] }); }} />}
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>(initial ?? { unit: 'PCS' });
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  const save = useMutation({
    mutationFn: async () => {
      if (initial) return (await api.patch(`/wms/products/${initial.id}`, form)).data;
      return (await api.post('/wms/products', form)).data;
    },
    onSuccess: onSaved,
  });
  const canSave = !!(form.sku && form.name);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[820px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">{initial ? 'Edit Product' : 'Add Product'}</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 grid grid-cols-3 gap-3">
          <F label="SKU Code *"><input value={form.sku ?? ''} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="SKU Name *"><input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Barcode"><input value={form.barcode ?? ''} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="Merchant"><select value={form.merchantId ?? ''} onChange={(e) => setForm({ ...form, merchantId: e.target.value ? Number(e.target.value) : null })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">— (shared)</option>{(merchants as any[]).map((m: any) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
          <F label="Warehouse"><select value={form.warehouseId ?? ''} onChange={(e) => setForm({ ...form, warehouseId: e.target.value ? Number(e.target.value) : null })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w: any) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
          <F label="Unit"><select value={form.unit ?? 'PCS'} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">{['PCS','KG','LTR','BOX','PAIR','PACK'].map(x => <option key={x}>{x}</option>)}</select></F>
          <F label="HSN Code"><input value={form.hsnCode ?? ''} onChange={(e) => setForm({ ...form, hsnCode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="GST %"><input type="number" value={form.gstPct ?? ''} onChange={(e) => setForm({ ...form, gstPct: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="MRP ₹"><input type="number" value={form.mrp ?? ''} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Selling ₹"><input type="number" value={form.sellingPrice ?? ''} onChange={(e) => setForm({ ...form, sellingPrice: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Cost ₹"><input type="number" value={form.costPrice ?? ''} onChange={(e) => setForm({ ...form, costPrice: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Weight (kg)"><input type="number" step="0.001" value={form.weight ?? ''} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Length (cm)"><input type="number" value={form.length ?? ''} onChange={(e) => setForm({ ...form, length: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Breadth (cm)"><input type="number" value={form.breadth ?? ''} onChange={(e) => setForm({ ...form, breadth: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Height (cm)"><input type="number" value={form.height ?? ''} onChange={(e) => setForm({ ...form, height: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Reorder Level"><input type="number" value={form.reorderLevel ?? ''} onChange={(e) => setForm({ ...form, reorderLevel: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Reorder Qty"><input type="number" value={form.reorderQty ?? ''} onChange={(e) => setForm({ ...form, reorderQty: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <div className="col-span-3 flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.isSerialized} onChange={(e) => setForm({ ...form, isSerialized: e.target.checked })} /> Serial-number tracked</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.isBatchTracked} onChange={(e) => setForm({ ...form, isBatchTracked: e.target.checked })} /> Batch / Expiry tracked</label>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3"><button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button><button onClick={() => save.mutate()} disabled={save.isPending || !canSave} title={!canSave ? 'SKU Code and SKU Name are required' : undefined} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save'}</button></div>
      </div>
    </div>
  );
}

// ───────── SUPPLIERS (merchant-scoped, auto code) ─────────
function SuppliersTab() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const { data = [] } = useQuery({ queryKey: ['wms', 'suppliers'], queryFn: async () => (await api.get('/wms/suppliers')).data });
  const { data: merchants = [] } = useWmsMerchants();
  const merchantName = (id: any) => (merchants as any[]).find((m) => m.id === id)?.companyName ?? (id ? `M#${id}` : '—');
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    data as any[],
    (r, t) => [r.code, r.name, merchantName(r.merchantId), r.gstin, r.mobile, r.city].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const save = useMutation({
    mutationFn: async () => {
      const url = editing ? `/wms/suppliers/${editing.id}` : '/wms/suppliers';
      return editing ? (await api.patch(url, form)).data : (await api.post(url, form)).data;
    },
    onSuccess: () => { setShow(false); setEditing(null); setForm({}); qc.invalidateQueries({ queryKey: ['wms', 'suppliers'] }); },
  });
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex justify-between mb-3">
        <div className="flex items-center gap-3"><h3 className="font-semibold">Suppliers</h3><WmsSearch value={q} onChange={setQ} placeholder="Search code / name / merchant / city" className="w-72" /></div>
        <div className="flex items-center gap-2"><ExportImportBar resource="wms/suppliers" onImported={() => qc.invalidateQueries({ queryKey: ['wms', 'suppliers'] })} /><button onClick={() => { setForm({}); setEditing(null); setShow(true); }} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> Add</button></div>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-emerald-50"><tr><th className="p-2 text-left">Code</th><th className="text-left">Name</th><th className="text-left">Merchant</th><th className="text-left">GSTIN</th><th className="text-left">Mobile</th><th className="text-left">City</th><th /></tr></thead>
        <tbody>
          {total === 0 ? <tr><td colSpan={7} className="text-center py-6 text-slate-400">{q ? 'No matches' : 'No suppliers yet'}</td></tr>
            : pageRows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 font-mono text-xs">{r.code}</td>
                <td>{r.name}</td>
                <td>{merchantName(r.merchantId)}</td>
                <td>{r.gstin ?? '—'}</td>
                <td>{r.mobile ?? '—'}</td>
                <td>{r.city ?? '—'}</td>
                <td><button onClick={() => { setEditing(r); setForm(r); setShow(true); }} className="text-emerald-700"><Pencil size={14} /></button></td>
              </tr>
            ))
          }
        </tbody>
      </table>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
          <div className="bg-white rounded-lg w-[620px]">
            <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">{editing ? 'Edit' : 'Add'} Supplier</div><button onClick={() => setShow(false)}><X size={18} /></button></div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <F label="Merchant *"><select value={form.merchantId ?? ''} onChange={(e) => setForm({ ...form, merchantId: e.target.value ? Number(e.target.value) : null })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
              <F label="Code (auto)"><input value={editing?.code ?? 'auto-generated'} disabled className="w-full border rounded px-2 py-1.5 text-sm bg-slate-50 text-slate-400 font-mono" /></F>
              <F label="Name *"><input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="GSTIN"><input value={form.gstin ?? ''} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
              <F label="Contact Name"><input value={form.contactName ?? ''} onChange={(e) => setForm({ ...form, contactName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="Mobile"><input value={form.mobile ?? ''} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="City"><input value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="State"><input value={form.state ?? ''} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-3"><button onClick={() => setShow(false)} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button><button onClick={() => save.mutate()} disabled={save.isPending || !form.merchantId || !form.name} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────── GRN ─────────
function GrnTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [show, setShow] = useState(false);
  const { data = [] } = useQuery({ queryKey: ['wms-grns'], queryFn: async () => (await api.get('/wms/grn')).data });
  const [active, setActive] = useState<any>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const dateFiltered = (data as any[]).filter((g) => wmsInDateRange(g.createdAt, from, to));
  const statusFiltered = status ? dateFiltered.filter((g) => g.status === status) : dateFiltered;
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    statusFiltered,
    (g, t) => [g.grnNo, g.supplier?.name, g.supplierName, g.invoiceNo, g.poNumber, g.receivedByName, g.status].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const countOf = (st: string) => dateFiltered.filter((g: any) => g.status === st).length;
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap"><h3 className="font-semibold">GRN — first inbound entry</h3><WmsSearch value={q} onChange={setQ} placeholder="Search GRN no / supplier / status" className="w-72" /><WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="GRN date" /></div>
        {!viewOnly && <div className="flex items-center gap-2"><ExportImportBar resource="wms/grn" onImported={() => qc.invalidateQueries({ queryKey: ['wms-grns'] })} /><button onClick={() => setShow(true)} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> New Inward (GRN)</button></div>}
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-slate-500">Recording an inward does NOT add stock — it goes to <b>Put away</b> next, then <b>QC</b>.</span>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm ml-auto">
          <option value="">All stages</option>
          <option value="PENDING_PUTAWAY">Awaiting putaway ({countOf('PENDING_PUTAWAY')})</option>
          <option value="PENDING_QC">Awaiting QC ({countOf('PENDING_QC')})</option>
          <option value="COMPLETED">Completed ({countOf('COMPLETED')})</option>
          <option value="CANCELLED">Cancelled ({countOf('CANCELLED')})</option>
        </select>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-emerald-50"><tr><th className="p-2 text-left">GRN No</th><th className="text-left">Supplier</th><th className="text-left">Invoice</th><th className="text-right">Qty</th><th className="text-right">Value</th><th>Stage</th><th>Date</th><th /></tr></thead>
        <tbody>
          {total === 0 ? <tr><td colSpan={8} className="text-center py-6 text-slate-400">{q || status ? 'No matches' : 'No GRNs yet'}</td></tr>
            : pageRows.map((g: any) => (
              <tr key={g.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => setActive(g)}>
                <td className="p-2 font-mono text-xs">{g.grnNo}</td>
                <td>{g.supplier?.name ?? g.supplierName ?? '—'}</td>
                <td className="text-xs">{g.invoiceNo ?? '—'}</td>
                <td className="text-right">{g.totalQty}</td>
                <td className="text-right">₹{Number(g.totalValue).toFixed(2)}</td>
                <td><Badge text={g.status} /></td>
                <td className="text-xs text-slate-500">{g.createdAt?.slice(0,10)}</td>
                <td className="text-xs text-emerald-700">Open →</td>
              </tr>
            ))
          }
        </tbody>
      </table>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && <GrnForm onClose={() => setShow(false)} onSaved={() => { setShow(false); qc.invalidateQueries({ queryKey: ['wms-grns'] }); }} />}
      {active && <GrnDetail grn={active} viewOnly={viewOnly} onClose={() => setActive(null)} onUpdated={() => { qc.invalidateQueries({ queryKey: ['wms-grns'] }); }} />}
    </div>
  );
}

function GrnForm({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ type: 'PURCHASE', items: [{ productId: '', expectedQty: 1, unitCost: 0 }] });
  const { data: prodData } = useQuery({ queryKey: ['wms-products-all'], queryFn: async () => (await api.get('/wms/products', { params: { pageSize: 100000 } })).data as any });
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  // Suppliers filtered to the chosen merchant (asked for on the supplier form).
  const { data: suppliers = [] } = useQuery({
    queryKey: ['wms-suppliers', form.merchantId],
    enabled: !!form.merchantId,
    queryFn: async () => (await api.get('/wms/suppliers', { params: { merchantId: form.merchantId } })).data,
  });
  const products = prodData?.items ?? [];
  const productOptions = (products as any[]).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}`, hint: p.merchantId ? `M#${p.merchantId}` : '' }));
  const setItem = (i: number, k: string, v: any) => setForm((f: any) => ({ ...f, items: f.items.map((x: any, idx: number) => idx === i ? { ...x, [k]: v } : x) }));
  const addRow = () => setForm((f: any) => ({ ...f, items: [...f.items, { productId: '', expectedQty: 1, unitCost: 0 }] }));
  const rmRow = (i: number) => setForm((f: any) => ({ ...f, items: f.items.filter((_: any, idx: number) => idx !== i) }));
  const save = useMutation({ mutationFn: async () => (await api.post('/wms/grn', form)).data, onSuccess: onSaved });
  const itemsValid = form.items.length > 0 && form.items.every((it: any) => !!it.productId);
  const canSave = !!form.warehouseId && !!form.merchantId && !!(form.invoiceNo ?? '').toString().trim() && itemsValid;
  const onPdf = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3_000_000) { alert('File too large (max 3 MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f: any) => ({ ...f, invoicePdfUrl: reader.result as string }));
    reader.readAsDataURL(file);
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[920px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">New GRN (Inward)</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {/* Warehouse first, then Merchant, then merchant-scoped Supplier */}
            <F label="Warehouse * (active)"><select value={form.warehouseId ?? ''} onChange={(e) => setForm({ ...form, warehouseId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
            <F label="Merchant *"><select value={form.merchantId ?? ''} onChange={(e) => setForm({ ...form, merchantId: Number(e.target.value), supplierId: null })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
            <F label="Supplier"><select value={form.supplierId ?? ''} onChange={(e) => setForm({ ...form, supplierId: e.target.value ? Number(e.target.value) : null })} disabled={!form.merchantId} className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-50"><option value="">{form.merchantId ? '—' : 'pick merchant first'}</option>{(suppliers as any[]).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></F>
            <F label="Vehicle No"><input value={form.vehicleNo ?? ''} onChange={(e) => setForm({ ...form, vehicleNo: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="LR No"><input value={form.lrNo ?? ''} onChange={(e) => setForm({ ...form, lrNo: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Invoice No *"><input value={form.invoiceNo ?? ''} onChange={(e) => setForm({ ...form, invoiceNo: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Invoice Date"><input type="date" value={form.invoiceDate ?? ''} onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Inbound Date"><input type="date" value={form.inboundDate ?? ''} onChange={(e) => setForm({ ...form, inboundDate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="PO Number"><input value={form.poNumber ?? ''} onChange={(e) => setForm({ ...form, poNumber: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Supplier/Vendor Name"><input value={form.supplierName ?? ''} onChange={(e) => setForm({ ...form, supplierName: e.target.value })} placeholder="free text if not in list" className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Invoice Amount"><input type="number" step="0.01" value={form.invoiceAmount ?? ''} onChange={(e) => setForm({ ...form, invoiceAmount: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Received By"><input value={form.receivedByName ?? ''} onChange={(e) => setForm({ ...form, receivedByName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><F label="Remarks"><input value={form.remarks ?? ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Invoice PDF / Image</label>
              <div className="flex items-center gap-2">
                <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={onPdf} className="text-xs" />
                {form.invoicePdfUrl && <a href={form.invoicePdfUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">View</a>}
              </div>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium mb-2">Items</div>
            {form.items.map((it: any, i: number) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                <div className="col-span-4"><F label="Product *"><SearchableSelect value={it.productId || ''} onChange={(v) => setItem(i, 'productId', v)} options={productOptions} placeholder="Search SKU / name…" /></F></div>
                <div className="col-span-1"><F label="Qty"><input type="number" value={it.expectedQty} onChange={(e) => setItem(i, 'expectedQty', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
                <div className="col-span-2"><F label="UOM"><input value={it.uom ?? ''} onChange={(e) => setItem(i, 'uom', e.target.value)} placeholder="PCS" className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
                <div className="col-span-2"><F label="Unit Cost"><input type="number" value={it.unitCost} onChange={(e) => setItem(i, 'unitCost', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
                <div className="col-span-2"><F label="Batch"><input value={it.batchNo ?? ''} onChange={(e) => setItem(i, 'batchNo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F></div>
                <div className="col-span-1">{form.items.length > 1 && <button onClick={() => rmRow(i)} className="border border-red-300 text-red-600 p-1.5 rounded"><Trash2 size={14} /></button>}</div>
              </div>
            ))}
            <button onClick={addRow} className="text-emerald-700 border border-emerald-300 px-3 py-1 rounded text-xs flex items-center gap-1"><Plus size={12} /> Add line</button>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-3"><div className="text-xs text-slate-500 mr-auto">Records the receipt only — stock enters inventory at Put away.</div><button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button><button onClick={() => save.mutate()} disabled={save.isPending || !canSave} title={!canSave ? 'Warehouse, Merchant, Invoice No and a Product on every line are required' : undefined} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Creating…' : 'Create GRN'}</button></div>
      </div>
    </div>
  );
}

// Seed editable line state from a GRN's items. The GRN screen edits WHAT ARRIVED; the Good/Bad split
// and the bin belong to Putaway, and the re-check to QC, so they are shown read-only here.
function seedGrnItems(g: any) {
  return (g.items ?? []).map((i: any) => ({
    id: i.id, productId: String(i.productId ?? i.product?.id ?? ''), sku: i.product?.sku, name: i.product?.name,
    receivedQty: Number(i.receivedQty ?? 0), uom: i.uom ?? '', unitCost: i.unitCost ?? '', batchNo: i.batchNo ?? '',
    goodQty: Number(i.goodQty ?? 0), badQty: Number(i.badQty ?? 0), bin: i.bin ?? '',
    putawayStatus: i.putawayStatus ?? 'PENDING', qcStatus: i.qcStatus ?? 'PENDING',
  }));
}

function GrnDetail({ grn, onClose, onUpdated, viewOnly }: any) {
  const [items, setItems] = useState<any[]>(seedGrnItems(grn));
  const { data: prodData } = useQuery({ queryKey: ['wms-products-all'], queryFn: async () => (await api.get('/wms/products', { params: { pageSize: 100000 } })).data as any });
  const products = prodData?.items ?? [];
  const productOptions = (products as any[]).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}`, hint: p.merchantId ? `M#${p.merchantId}` : '' }));
  const setIt = (idx: number, k: string, v: any) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, [k]: v } : x));
  const addLine = () => setItems((arr) => [...arr, { productId: '', receivedQty: 1, uom: '', unitCost: '', batchNo: '', goodQty: 0, badQty: 0, bin: '', putawayStatus: 'PENDING', qcStatus: 'PENDING' }]);
  const rmLine = (idx: number) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const hasUnsaved = items.some((i) => !i.id || !i.productId);
  // Lines are frozen once putaway has posted them — changing a received qty then would desync stock.
  const locked = grn.status !== 'PENDING_PUTAWAY';

  // Editable GRN header (Inbound Entry) fields.
  const [hdr, setHdr] = useState<any>({
    invoiceNo: grn.invoiceNo ?? '', invoiceDate: grn.invoiceDate?.slice(0, 10) ?? '', inboundDate: grn.inboundDate?.slice(0, 10) ?? '',
    poNumber: grn.poNumber ?? '', supplierName: grn.supplierName ?? grn.supplier?.name ?? '', invoiceAmount: grn.invoiceAmount ?? '',
    receivedByName: grn.receivedByName ?? '', remarks: grn.remarks ?? '', invoicePdfUrl: grn.invoicePdfUrl ?? '',
  });
  const setH = (k: string, v: any) => setHdr((p: any) => ({ ...p, [k]: v }));

  // Save header + product lines (add/edit/remove). Reseeds from the fresh GRN so new lines get their ids.
  const saveDetails = useMutation({
    mutationFn: async () => (await api.patch(`/wms/grn/${grn.id}`, {
      ...hdr,
      items: items.map((i) => ({ id: i.id, productId: i.productId, receivedQty: Number(i.receivedQty) || 0, expectedQty: Number(i.receivedQty) || 0, uom: i.uom, unitCost: i.unitCost, batchNo: i.batchNo })),
    })).data,
    onSuccess: (data: any) => { setItems(seedGrnItems(data)); alert('Inward details & products saved'); onUpdated(); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Save failed'),
  });

  const onPdf = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3_000_000) { alert('File too large (max 3 MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setH('invoicePdfUrl', reader.result as string);
    reader.readAsDataURL(file);
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[1040px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">{grn.grnNo} <Badge text={grn.status} /></div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 space-y-4">
          {/* Inward / Inbound entry details (editable) */}
          <div>
            <div className="text-sm font-medium mb-2">Inward Details</div>
            <div className="grid grid-cols-3 gap-3">
              <F label="Inbound Date"><input type="date" value={hdr.inboundDate} onChange={(e) => setH('inboundDate', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="Invoice Date"><input type="date" value={hdr.invoiceDate} onChange={(e) => setH('invoiceDate', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="Invoice Number"><input value={hdr.invoiceNo} onChange={(e) => setH('invoiceNo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="Supplier / Vendor Name"><input value={hdr.supplierName} onChange={(e) => setH('supplierName', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="PO Number"><input value={hdr.poNumber} onChange={(e) => setH('poNumber', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="Invoice Amount"><input type="number" step="0.01" value={hdr.invoiceAmount} onChange={(e) => setH('invoiceAmount', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="Received By"><input value={hdr.receivedByName} onChange={(e) => setH('receivedByName', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <div className="col-span-2"><F label="Remarks"><input value={hdr.remarks} onChange={(e) => setH('remarks', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-500 mb-1">Invoice PDF / Image</label>
                <div className="flex items-center gap-3">
                  <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={onPdf} className="text-xs" />
                  {hdr.invoicePdfUrl && <a href={hdr.invoicePdfUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">View uploaded invoice</a>}
                  {hdr.invoicePdfUrl && <button onClick={() => setH('invoicePdfUrl', '')} className="text-xs text-red-600">Remove</button>}
                </div>
              </div>
            </div>
          </div>

          {/* Products + QC — add / edit lines (same columns as New GRN) plus Good/Bad */}
          <div>
            <div className="text-sm font-medium mb-2">Products received <span className="text-xs text-slate-400">(the Good / Bad split and the bin are set at Put away)</span></div>
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-xs"><tr>
                <th className="p-2 text-left" style={{ minWidth: 220 }}>Product</th><th className="text-right">Recv Qty</th><th className="text-left">UOM</th><th className="text-right">Unit Cost</th><th className="text-left">Batch</th>
                <th className="text-right">Good</th><th className="text-right">Bad</th><th className="text-left">Bin</th><th className="text-left">Putaway</th><th className="text-left">QC</th><th></th>
              </tr></thead>
              <tbody>
                {items.map((i: any, idx: number) => (
                  <tr key={i.id ?? `new-${idx}`} className="border-t">
                    <td className="p-2">{i.id
                      ? <span>{i.sku} — {i.name}</span>
                      : <SearchableSelect value={i.productId || ''} onChange={(v) => setIt(idx, 'productId', v)} options={productOptions} placeholder="Search SKU / name…" />}</td>
                    <td className="text-right"><input type="number" min={0} disabled={locked || viewOnly} value={i.receivedQty} onChange={(e) => setIt(idx, 'receivedQty', e.target.value)} className="w-16 border rounded px-1 text-sm text-right disabled:bg-slate-50" /></td>
                    <td><input value={i.uom ?? ''} disabled={locked || viewOnly} onChange={(e) => setIt(idx, 'uom', e.target.value)} placeholder="PCS" className="w-16 border rounded px-1 text-sm disabled:bg-slate-50" /></td>
                    <td className="text-right"><input type="number" disabled={locked || viewOnly} value={i.unitCost ?? ''} onChange={(e) => setIt(idx, 'unitCost', e.target.value)} className="w-20 border rounded px-1 text-sm text-right disabled:bg-slate-50" /></td>
                    <td><input value={i.batchNo ?? ''} disabled={locked || viewOnly} onChange={(e) => setIt(idx, 'batchNo', e.target.value)} className="w-20 border rounded px-1 text-sm font-mono disabled:bg-slate-50" /></td>
                    <td className="text-right text-emerald-700">{i.putawayStatus === 'DONE' ? i.goodQty : '—'}</td>
                    <td className="text-right text-red-600">{i.putawayStatus === 'DONE' ? i.badQty : '—'}</td>
                    <td className="font-mono text-xs">{i.bin || '—'}</td>
                    <td><Badge text={i.putawayStatus ?? 'PENDING'} /></td>
                    <td><Badge text={i.qcStatus ?? 'PENDING'} /></td>
                    <td>{!locked && !viewOnly && items.length > 1 && <button onClick={() => rmLine(idx)} className="border border-red-300 text-red-600 p-1 rounded"><Trash2 size={13} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 flex items-center justify-between">
              {!locked && !viewOnly && <button onClick={addLine} className="text-emerald-700 border border-emerald-300 px-3 py-1 rounded text-xs flex items-center gap-1"><Plus size={12} /> Add product</button>}
              {locked && <span className="text-xs text-slate-500">Lines are locked once putaway has posted this stock — correct it with a stock adjustment instead.</span>}
              {hasUnsaved && <span className="text-xs text-amber-600">Unsaved new products — click &quot;Save Inward Details &amp; Products&quot;.</span>}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-3">
          <div className="text-xs text-slate-500 mr-auto">
            {grn.status === 'PENDING_PUTAWAY' ? 'Next step: Inbound → Put away, where the Good / Bad split posts this into inventory.'
              : grn.status === 'PENDING_QC' ? 'Stock is in inventory. Next step: Inbound → QC.'
              : 'This inward is complete.'}
          </div>
          <button onClick={onClose} className="border px-4 py-1.5 rounded text-sm">Close</button>
          {!viewOnly && <button onClick={() => saveDetails.mutate()} disabled={saveDetails.isPending} className="bg-emerald-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50">{saveDetails.isPending ? 'Saving…' : 'Save Inward Details'}</button>}
        </div>
      </div>
    </div>
  );
}

// ───────── DISPATCH (manual register) ─────────
function DispatchTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [show, setShow] = useState(false);
  const [viewing, setViewing] = useState<any>(null);
  const { data = [] } = useQuery({ queryKey: ['wms-dispatch'], queryFn: async () => (await api.get('/wms/dispatch')).data as any[] });
  const { data: merchants = [] } = useWmsMerchants();
  const mName = (id: any) => (merchants as any[]).find((m) => m.id === id)?.companyName ?? (id ? `M#${id}` : '—');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const dateFiltered = (data as any[]).filter((d) => wmsInDateRange(d.dispatchDate, from, to));
  const { q, setQ, page, setPage, total, offset, pageRows } = useSearchPaged(
    dateFiltered,
    (d, t) => [d.invoiceNumber, d.docketNumber, mName(d.merchantId), d.customerBillTo, d.skuCode, d.courier].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );

  // Correcting a dispatch means cancelling it — that writes the stock back and leaves both the
  // original outward and the reversal in the ledger. A reason is required.
  const cancel = useMutation({
    mutationFn: async ({ id, reason }: { id: any; reason: string }) => (await api.patch(`/wms/dispatch/${id}/cancel`, { reason })).data,
    onSuccess: (r: any) => { qc.invalidateQueries({ queryKey: ['wms-dispatch'] }); qc.invalidateQueries({ queryKey: ['wms-stock'] }); alert(`Dispatch cancelled — ${r.restored} unit(s) returned to stock.`); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not cancel the dispatch'),
  });
  const cancelDispatch = (d: any) => {
    const reason = window.prompt(`Cancel dispatch for invoice ${d.invoiceNumber ?? ''} / SKU ${d.skuCode ?? ''}?\n\nThe dispatched quantity goes back into stock. Give a reason:`);
    if (!reason || !reason.trim()) return;
    cancel.mutate({ id: d.id, reason: reason.trim() });
  };

  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap"><h3 className="font-semibold">Dispatch Register</h3><WmsSearch value={q} onChange={setQ} placeholder="Search invoice / docket / merchant / SKU" className="w-72" /><WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="Dispatch date" /></div>
        {!viewOnly && <div className="flex items-center gap-2"><ExportImportBar resource="wms/dispatch" onImported={() => qc.invalidateQueries({ queryKey: ['wms-dispatch'] })} /><button onClick={() => setShow(true)} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> Add Dispatch</button></div>}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">S No</th><th className="text-left">Dispatch Date</th><th className="text-left">Invoice No</th><th className="text-left">Docket</th>
            {!viewOnly && <th className="text-left">Merchant</th>}<th className="text-left">Bill To</th><th className="text-left">SKU Code</th><th className="text-right">Qty</th>
            <th className="text-left">Courier</th><th className="text-left">Requested By</th><th className="text-left">Approved By</th><th className="text-left">Dispatched By</th>
            <th className="text-center">Status</th><th className="text-center">View</th>
          </tr></thead>
          <tbody>
            {total === 0 ? <tr><td colSpan={viewOnly ? 13 : 14} className="text-center py-6 text-slate-400">{q ? 'No matches' : 'No dispatches yet'}</td></tr>
              : pageRows.map((d, i) => (
                <tr key={d.id} className={`border-t ${d.status === 'CANCELLED' ? 'bg-slate-50 text-slate-400 line-through' : ''}`}>
                  <td className="p-2">{offset + i + 1}</td>
                  <td className="text-xs">{d.dispatchDate?.slice(0, 10) ?? '—'}</td>
                  <td className="font-mono text-xs">{d.invoiceNumber ?? '—'}</td>
                  <td className="font-mono text-xs">{d.docketNumber ?? '—'}</td>
                  {!viewOnly && <td className="text-xs">{mName(d.merchantId)}</td>}
                  <td>{d.customerBillTo ?? '—'}</td>
                  <td className="font-mono text-xs">{d.skuCode ?? '—'}</td>
                  <td className="text-right">{d.qtyDispatched ?? '—'}</td>
                  <td className="text-xs">{d.courier ?? '—'}</td>
                  <td className="text-xs">{d.requestedByName ?? '—'}</td>
                  <td className="text-xs">{d.approvedByName ?? '—'}</td>
                  <td className="text-xs">{d.dispatchedByName ?? '—'}</td>
                  <td className="text-center">
                    <Badge text={d.status ?? 'DISPATCHED'} />
                    {/* A completed dispatch is never edited — cancelling is what puts the stock back. */}
                    {!viewOnly && d.status !== 'CANCELLED' && (
                      <button onClick={() => cancelDispatch(d)} title="Cancel this dispatch and return the stock"
                        className="ml-1 text-red-600 hover:bg-red-50 px-1 rounded text-xs no-underline">Cancel</button>
                    )}
                  </td>
                  <td className="text-center"><button onClick={() => setViewing(d)} title="View full details" className="text-emerald-700 hover:bg-emerald-50 p-1 rounded"><Eye size={15} /></button></td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && <DispatchForm onClose={() => setShow(false)} onSaved={() => { setShow(false); qc.invalidateQueries({ queryKey: ['wms-dispatch'] }); }} />}
      {viewing && <DispatchViewModal row={viewing} merchantName={mName(viewing.merchantId)} onClose={() => setViewing(null)} />}
    </div>
  );
}

// Read-only detail view of one dispatch entry — shows every field, including those hidden in the list.
function DispatchViewModal({ row, merchantName, onClose }: { row: any; merchantName: string; onClose: () => void }) {
  const fmtDate = (v: any) => (v ? String(v).slice(0, 10) : '—');
  const val = (v: any) => (v === null || v === undefined || v === '' ? '—' : String(v));
  const fields: [string, any][] = [
    ['Dispatch Date', fmtDate(row.dispatchDate)],
    ['Merchant', merchantName],
    ['Invoice Number', val(row.invoiceNumber)],
    ['Invoice Date', fmtDate(row.invoiceDate)],
    ['Docket Number', val(row.docketNumber)],
    ['Customer (Bill To)', val(row.customerBillTo)],
    ['Client (Ship To)', val(row.clientShipTo)],
    ['SKU Name', val(row.skuName)],
    ['SKU Code', val(row.skuCode)],
    ['Qty Dispatched', val(row.qtyDispatched)],
    ['Unit of Measure', val(row.unitOfMeasure)],
    ['Requested Mode', val(row.requestedMode)],
    ['Requested By', val(row.requestedByName)],
    ['Approved By', val(row.approvedByName)],
    ['Dispatched By', val(row.dispatchedByName)],
    ['Invoice Receiving Date', fmtDate(row.invoiceReceivingDate)],
    ['Courier', val(row.courier)],
    ['Dimension', val(row.dimension)],
    ['Weight (kg)', val(row.weight)],
    ['Box Qty', val(row.boxQty)],
    ['Mob No', val(row.mobileNo)],
  ];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[860px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><Eye size={16} /> Dispatch Details {row.invoiceNumber ? `— ${row.invoiceNumber}` : ''}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-3 gap-x-6 gap-y-3">
          {fields.map(([label, value]) => (
            <div key={label}>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
              <div className="text-sm text-slate-800 break-words">{value}</div>
            </div>
          ))}
          <div className="col-span-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Remarks</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{val(row.remarks)}</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end"><button onClick={onClose} className="border px-4 py-1.5 rounded text-sm">Close</button></div>
      </div>
    </div>
  );
}

function DispatchForm({ onClose, onSaved }: any) {
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  const { data: users = [] } = useUsersLight();
  const userList = Array.isArray(users) ? users : (users as any)?.items ?? [];
  const [form, setForm] = useState<any>({ unitOfMeasure: 'PCS' });
  const [err, setErr] = useState('');
  // Shown only after the server has refused this Invoice + SKU as an already-dispatched duplicate.
  // Nothing goes out twice without someone writing down why.
  const [needsRedispatch, setNeedsRedispatch] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const save = useMutation({
    mutationFn: async () => (await api.post('/wms/dispatch', form)).data,
    onSuccess: onSaved,
    onError: (e: any) => {
      const msg = e?.response?.data?.message || 'Could not save dispatch. Please try again.';
      if (/^Duplicate entry/i.test(String(msg))) setNeedsRedispatch(true);
      setErr(msg);
    },
  });
  const userOpts = userList.map((u: any) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName ?? ''}{u.empCode ? ` (${u.empCode})` : ''}</option>);

  // Live stock availability for the entered SKU at the chosen merchant + warehouse.
  const skuKey = (form.skuCode ?? '').toString().trim();
  const nameKey = (form.skuName ?? '').toString().trim();
  const canCheck = !!(form.merchantId && form.warehouseId && (skuKey || nameKey));
  const { data: avail } = useQuery({
    queryKey: ['wms-avail', skuKey, nameKey, form.merchantId, form.warehouseId],
    enabled: canCheck,
    queryFn: async () => (await api.get('/wms/inventory/available', { params: { skuCode: skuKey, skuName: nameKey, merchantId: form.merchantId, warehouseId: form.warehouseId } })).data as any,
  });
  const qtyNum = Number(form.qtyDispatched);
  const overStock = !!avail?.found && Number.isFinite(qtyNum) && qtyNum > (avail.available ?? 0);

  // Fields that must be filled before a dispatch can be saved.
  const REQUIRED = ['merchantId', 'warehouseId', 'dispatchDate', 'invoiceDate', 'invoiceNumber', 'docketNumber', 'customerBillTo', 'clientShipTo', 'skuName', 'skuCode', 'qtyDispatched', 'mobileNo'];
  const incomplete = REQUIRED.some((k) => form[k] === undefined || form[k] === null || String(form[k]).trim() === '');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[980px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">Add Dispatch (manual)</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 grid grid-cols-4 gap-3">
          <F label="Merchant Name *"><select value={form.merchantId ?? ''} onChange={(e) => set('merchantId', e.target.value ? Number(e.target.value) : null)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
          <F label="Warehouse Name *"><select value={form.warehouseId ?? ''} onChange={(e) => set('warehouseId', e.target.value ? Number(e.target.value) : null)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
          <F label="Dispatch Date *"><input type="date" value={form.dispatchDate ?? ''} onChange={(e) => set('dispatchDate', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Invoice Date *"><input type="date" value={form.invoiceDate ?? ''} onChange={(e) => set('invoiceDate', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Invoice Number *"><input value={form.invoiceNumber ?? ''} onChange={(e) => set('invoiceNumber', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="Docket Number *"><input value={form.docketNumber ?? ''} onChange={(e) => set('docketNumber', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="Customer Name (Bill To) *"><input value={form.customerBillTo ?? ''} onChange={(e) => set('customerBillTo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Client Name (Ship To) *"><input value={form.clientShipTo ?? ''} onChange={(e) => set('clientShipTo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="SKU Name *"><input value={form.skuName ?? ''} onChange={(e) => set('skuName', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="SKU Code *"><input value={form.skuCode ?? ''} onChange={(e) => set('skuCode', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="Quantity Dispatched *"><input type="number" value={form.qtyDispatched ?? ''} onChange={(e) => set('qtyDispatched', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Unit of Measure"><select value={form.unitOfMeasure ?? 'PCS'} onChange={(e) => set('unitOfMeasure', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">{['PCS','KG','LTR','BOX','PAIR','PACK'].map(x => <option key={x}>{x}</option>)}</select></F>
          {canCheck && avail && (
            <div className="col-span-4">
              {avail.found ? (
                <div className={`text-xs rounded px-3 py-2 border ${overStock ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                  Available in stock for <span className="font-mono">{avail.sku}</span> at this warehouse: <b>{avail.available}</b>{avail.damaged ? ` · damaged ${avail.damaged}` : ''}
                  {overStock && <span className="block mt-0.5 font-medium">You entered {qtyNum} — that's more than the {avail.available} in stock. Reduce the quantity or add stock first.</span>}
                </div>
              ) : (
                <div className="text-xs rounded px-3 py-2 border bg-red-50 text-red-700 border-red-200">
                  This SKU is not in the product master, so stock cannot be reduced for it — the dispatch will be refused. Check the SKU Code, or add the product under Warehouses → Products first.
                </div>
              )}
            </div>
          )}
          <F label="Requested Mode"><input value={form.requestedMode ?? ''} onChange={(e) => set('requestedMode', e.target.value)} placeholder="Email / Call / Portal" className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Requested By"><select value={form.requestedById ?? ''} onChange={(e) => set('requestedById', e.target.value ? Number(e.target.value) : null)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{userOpts}</select></F>
          <F label="Approved By"><select value={form.approvedById ?? ''} onChange={(e) => set('approvedById', e.target.value ? Number(e.target.value) : null)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{userOpts}</select></F>
          <F label="Dispatched By"><select value={form.dispatchedById ?? ''} onChange={(e) => set('dispatchedById', e.target.value ? Number(e.target.value) : null)} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{userOpts}</select></F>
          <F label="Invoice Receiving Date"><input type="date" value={form.invoiceReceivingDate ?? ''} onChange={(e) => set('invoiceReceivingDate', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Courier"><input value={form.courier ?? ''} onChange={(e) => set('courier', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Dimension"><input value={form.dimension ?? ''} onChange={(e) => set('dimension', e.target.value)} placeholder="L×B×H" className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Weight (kg)"><input type="number" step="0.001" value={form.weight ?? ''} onChange={(e) => set('weight', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Box Qty"><input type="number" value={form.boxQty ?? ''} onChange={(e) => set('boxQty', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Mob No *"><input value={form.mobileNo ?? ''} onChange={(e) => set('mobileNo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <div className="col-span-4"><F label="Remarks"><textarea value={form.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} rows={2} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
          {needsRedispatch && (
            <div className="col-span-4 border border-amber-300 bg-amber-50 rounded px-3 py-2">
              <div className="text-xs text-amber-800 mb-2">
                This Invoice + SKU has already been dispatched from this warehouse. If it really is going out again, say why — the reason is stored against the second movement.
              </div>
              <F label="Re-dispatch reason *">
                <input value={form.redispatchRemark ?? ''} onChange={(e) => set('redispatchRemark', e.target.value)}
                  placeholder="e.g. first shipment lost in transit, replacement sent"
                  className="w-full border rounded px-2 py-1.5 text-sm" />
              </F>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-3">
          {err && <div className="text-xs text-red-600 mr-auto">{err}</div>}
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => { setErr(''); save.mutate(); }}
            disabled={save.isPending || incomplete || overStock || (needsRedispatch && !String(form.redispatchRemark ?? '').trim())}
            title={overStock ? 'Dispatch quantity exceeds available stock' : incomplete ? 'Fill all required (*) fields' : needsRedispatch ? 'Give a re-dispatch reason' : ''}
            className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : needsRedispatch ? 'Save as Re-dispatch' : 'Save Dispatch'}</button>
        </div>
      </div>
    </div>
  );
}

// ───────── RTO ─────────
// ───────── ALLOCATIONS ─────────
function AllocationsTab() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const { data = [] } = useQuery({ queryKey: ['wms-alloc'], queryFn: async () => (await api.get('/wms/allocations')).data });
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    data as any[],
    (a, t) => [a.merchant?.companyName, a.warehouse?.name, a.status].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex justify-between mb-3">
        <div className="flex items-center gap-3"><h3 className="font-semibold">Merchants — Space Allocation &amp; Rate Card</h3><WmsSearch value={q} onChange={setQ} placeholder="Search merchant / warehouse / status" className="w-72" /></div>
        <div className="flex items-center gap-2"><ExportImportBar resource="wms/allocations" onImported={() => qc.invalidateQueries({ queryKey: ['wms-alloc'] })} />
        <button onClick={() => setShow(true)} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> Allocate Space</button></div>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-emerald-50"><tr>
          <th className="p-2 text-left">Merchant</th><th className="text-left">Warehouse</th><th className="text-right">Sq Ft</th>
          <th className="text-right">Rent/sqft/mo</th><th className="text-right">In / Out Fee</th><th className="text-right">Manpower/day</th>
          <th className="text-right">Load / Unload</th><th className="text-right">Packaging</th><th>Status</th><th>Start</th>
        </tr></thead>
        <tbody>
          {total === 0 ? <tr><td colSpan={10} className="text-center py-6 text-slate-400">{q ? 'No matches' : 'No allocations yet'}</td></tr>
            : pageRows.map((a: any) => (
              <tr key={a.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => { setEditing(a); setShow(true); }}>
                <td className="p-2">{a.merchant?.companyName ?? `M#${a.merchantId}`}</td>
                <td>{a.warehouse?.name ?? `W#${a.warehouseId}`}</td>
                <td className="text-right">{Number(a.allocatedAreaSqFt).toFixed(0)}</td>
                <td className="text-right">{rate(a.rentPerSqFtPerMonth)}</td>
                <td className="text-right">{rate(a.handlingInwardPerUnit)} / {rate(a.handlingOutwardPerUnit)}</td>
                <td className="text-right">{rate(a.manpowerPerManDay)}</td>
                <td className="text-right">{rate(a.loadingPerUnit)} / {rate(a.unloadingPerUnit)}</td>
                <td className="text-right">{rate(a.packagingPerUnit)}</td>
                <td><Badge text={a.status} /></td>
                <td className="text-xs">{a.startDate?.slice(0,10)}</td>
              </tr>
            ))
          }
        </tbody>
      </table>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && <AllocationForm initial={editing} onClose={() => { setShow(false); setEditing(null); }} onSaved={() => { setShow(false); setEditing(null); qc.invalidateQueries({ queryKey: ['wms-alloc'] }); }} />}
    </div>
  );
}

// Blank (rather than 0) for an unset rate — 0 would read as "free", which is a different statement.
function rate(v: any) { return v == null || v === '' ? '—' : `₹${Number(v)}`; }

function AllocationForm({ initial, onClose, onSaved }: any) {
  const { data: merchantList = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  const [form, setForm] = useState<any>(() => initial
    ? { ...initial, startDate: initial.startDate?.slice(0, 10) ?? '', endDate: initial.endDate?.slice(0, 10) ?? '' }
    : { startDate: new Date().toISOString().slice(0, 10), allocatedAreaSqFt: 100, status: 'ACTIVE' });
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const money = (k: string, label: string) => (
    <F label={label}><input type="number" step="0.01" value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
  );
  const save = useMutation({
    mutationFn: async () => (await api.post('/wms/allocations', form)).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not save'),
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[860px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">{initial ? 'Edit' : 'New'} Merchant Allocation &amp; Rate Card</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <F label="Merchant *"><select value={form.merchantId ?? ''} disabled={!!initial} onChange={(e) => set('merchantId', Number(e.target.value))} className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-50"><option value="">—</option>{merchantList.map((m: any) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
            <F label="Warehouse *"><select value={form.warehouseId ?? ''} disabled={!!initial} onChange={(e) => set('warehouseId', Number(e.target.value))} className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-50"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
            <F label="Status"><select value={form.status ?? 'ACTIVE'} onChange={(e) => set('status', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">{['ACTIVE','EXPIRED','TERMINATED'].map((x) => <option key={x}>{x}</option>)}</select></F>
            <F label="Area (sq ft) *"><input type="number" value={form.allocatedAreaSqFt ?? ''} onChange={(e) => set('allocatedAreaSqFt', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Start Date *"><input type="date" value={form.startDate ?? ''} onChange={(e) => set('startDate', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="End Date"><input type="date" value={form.endDate ?? ''} onChange={(e) => set('endDate', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Rate Card <span className="text-xs text-slate-400 font-normal">— what the Billing Report prices this merchant&apos;s activity against. Leave a rate blank if it is not charged.</span></div>
            <div className="grid grid-cols-3 gap-3">
              {money('rentPerSqFtPerMonth', 'Space — rent / sq ft / month')}
              {money('manpowerPerManDay', 'Manpower / man-day')}
              {money('handlingInwardPerUnit', 'Handling inward / unit')}
              {money('handlingOutwardPerUnit', 'Handling outward / unit')}
              {money('loadingPerUnit', 'Loading / unit')}
              {money('unloadingPerUnit', 'Unloading / unit')}
              {money('packagingPerUnit', 'Packaging / unit')}
              {money('codFeePerUnit', 'COD fee / unit')}
              {money('otherMonthlyCharge', 'Other — fixed / month')}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <F label="Other charge — what for"><input value={form.otherChargeNote ?? ''} onChange={(e) => set('otherChargeNote', e.target.value)} placeholder="e.g. security, utilities" className="w-full border rounded px-2 py-1.5 text-sm" /></F>
              <F label="Notes"><input value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3"><button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button><button onClick={() => save.mutate()} disabled={save.isPending || !form.merchantId || !form.warehouseId} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save Allocation'}</button></div>
      </div>
    </div>
  );
}


// ───────── WMS WAREHOUSES ─────────
function WarehousesTab() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  // `overview` returns the same rows as /wms/warehouses plus the per-warehouse figures the columns show.
  const { data = [] } = useQuery({ queryKey: ['wms-whs-overview'], queryFn: async () => (await api.get('/wms/warehouses/overview')).data });
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    data as any[],
    (w, t) => [w.code, w.name, w.type, w.city, w.status, w.manager ? `${w.manager.firstName} ${w.manager.lastName ?? ''}` : ''].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );

  return (
    <div className="space-y-3">
      <div className="bg-white border rounded-xl p-4">
        <div className="flex justify-between mb-3">
          <div className="flex items-center gap-3"><h3 className="font-semibold flex items-center gap-2"><Warehouse size={16} /> Company-owned Warehouses</h3><WmsSearch value={q} onChange={setQ} placeholder="Search code / name / city / manager" className="w-72" /></div>
          <div className="flex items-center gap-2"><ExportImportBar resource="wms/warehouses" onImported={() => qc.invalidateQueries({ queryKey: ['wms-whs-overview'] })} /><button onClick={() => { setEditing(null); setShow(true); }} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> Add Warehouse</button></div>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">Code</th><th className="text-left">Name</th><th className="text-left">City</th>
            <th className="text-right">Merchants</th><th className="text-right">Total Inventory</th><th className="text-right">Inventory Cost</th>
            <th className="text-right">Space Used</th><th className="text-right">Staff</th><th>Status</th><th />
          </tr></thead>
          <tbody>
            {total === 0 ? <tr><td colSpan={10} className="text-center py-6 text-slate-400">{q ? 'No matches' : 'No warehouses yet'}</td></tr>
              : pageRows.map((w: any) => (
                <tr key={w.id} className="border-t hover:bg-slate-50">
                  <td className="p-2 font-mono text-xs">{w.code}</td>
                  <td>{w.name}<div className="text-[11px] text-slate-400">{w.type}{w.manager ? ` · ${w.manager.firstName} ${w.manager.lastName ?? ''}` : ''}</div></td>
                  <td>{w.city ?? '—'}</td>
                  <td className="text-right">{w.merchantCount ?? 0}</td>
                  <td className="text-right">{Number(w.totalUnits ?? 0).toLocaleString()}{Number(w.totalDamagedUnits) > 0 ? <span className="text-red-500 text-[11px]"> +{w.totalDamagedUnits} dmg</span> : null}</td>
                  <td className="text-right">₹{Number(w.totalInventoryCost ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                  <td className="text-right">
                    {Number(w.allocatedSqFt ?? 0).toLocaleString()} / {Number(w.capacitySqFt ?? 0).toLocaleString()}
                    <div className={`text-[11px] ${w.utilizationPct == null ? 'text-slate-400' : w.utilizationPct >= 90 ? 'text-red-600' : w.utilizationPct >= 70 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {w.utilizationPct == null ? 'capacity not set' : `${w.utilizationPct}% used`}
                    </div>
                  </td>
                  <td className="text-right text-emerald-700 font-medium">{(w.staff ?? []).filter((s: any) => s.isActive).length}</td>
                  <td><Badge text={w.status} /></td>
                  <td className="text-right">
                    <button onClick={() => setActiveId(w.id)} className="text-emerald-700 text-xs mr-2">Staff</button>
                    <button onClick={() => { setEditing(w); setShow(true); }} className="text-emerald-700"><Pencil size={14} /></button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
        <WmsPageBar page={page} total={total} onPage={setPage} />
      </div>
      {show && <WhForm initial={editing} onClose={() => { setShow(false); setEditing(null); }} onSaved={() => { setShow(false); setEditing(null); qc.invalidateQueries({ queryKey: ['wms-whs-overview'] }); }} />}
      {activeId !== null && <StaffPanel warehouseId={activeId} onClose={() => setActiveId(null)} />}
    </div>
  );
}

function WhForm({ initial, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>(initial ?? { type: 'FULFILLMENT', ownership: 'OWNED', status: 'ACTIVE', country: 'India' });
  const { data: users = [] } = useQuery({ queryKey: ['users-light'], queryFn: async () => (await api.get('/users', { params: { pageSize: 500 } })).data });
  const userList = Array.isArray(users) ? users : (users as any)?.items ?? [];
  const save = useMutation({
    mutationFn: async () => {
      if (initial) return (await api.patch(`/wms/warehouses/${initial.id}`, form)).data;
      return (await api.post('/wms/warehouses', form)).data;
    },
    onSuccess: onSaved,
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[820px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">{initial ? 'Edit Warehouse' : 'Add Company Warehouse'}</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 grid grid-cols-3 gap-3">
          <F label="Code *"><input value={form.code ?? ''} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="WH-NDA-01" className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="Name *"><input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Type"><select value={form.type ?? 'FULFILLMENT'} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">{['FULFILLMENT','TRANSIT','RTO_CENTER','DARK_STORE','COLD_STORAGE'].map(x => <option key={x}>{x}</option>)}</select></F>
          <F label="Ownership"><select value={form.ownership ?? 'OWNED'} onChange={(e) => setForm({ ...form, ownership: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">{['OWNED','LEASED','3PL_MANAGED'].map(x => <option key={x}>{x}</option>)}</select></F>
          <F label="Status"><select value={form.status ?? 'ACTIVE'} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">{['ACTIVE','INACTIVE','MAINTENANCE'].map(x => <option key={x}>{x}</option>)}</select></F>
          <F label="Manager"><select value={form.managerId ?? ''} onChange={(e) => setForm({ ...form, managerId: e.target.value ? Number(e.target.value) : null })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{userList.map((u: any) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName ?? ''} — {u.userType}</option>)}</select></F>
          <F label="Total Area (sq ft) *"><input type="number" value={form.totalAreaSqFt ?? ''} onChange={(e) => setForm({ ...form, totalAreaSqFt: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Cubic Ft"><input type="number" value={form.totalCubicFt ?? ''} onChange={(e) => setForm({ ...form, totalCubicFt: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Operating Hours"><input value={form.operatingHours ?? ''} onChange={(e) => setForm({ ...form, operatingHours: e.target.value })} placeholder="9:00 AM - 9:00 PM" className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Weekly Off"><input value={form.weeklyOff ?? ''} onChange={(e) => setForm({ ...form, weeklyOff: e.target.value })} placeholder="Sunday" className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Contact Name"><input value={form.contactName ?? ''} onChange={(e) => setForm({ ...form, contactName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Contact Mobile"><input value={form.contactMobile ?? ''} onChange={(e) => setForm({ ...form, contactMobile: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Contact Email"><input value={form.contactEmail ?? ''} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Pincode"><input value={form.pincode ?? ''} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="City"><input value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="State"><input value={form.state ?? ''} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <div className="col-span-3"><F label="Address Line 1"><input value={form.addressLine1 ?? ''} onChange={(e) => setForm({ ...form, addressLine1: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
          <div className="col-span-3"><F label="Address Line 2"><input value={form.addressLine2 ?? ''} onChange={(e) => setForm({ ...form, addressLine2: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
          <div className="col-span-3"><F label="Notes"><textarea value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3"><button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button><button onClick={() => save.mutate()} disabled={save.isPending || !form.code || !form.name} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save Warehouse'}</button></div>
      </div>
    </div>
  );
}

function StaffPanel({ warehouseId, onClose }: { warehouseId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: w } = useQuery({ queryKey: ['wms-wh', warehouseId], queryFn: async () => (await api.get(`/wms/warehouses/${warehouseId}`)).data as any });
  const { data: users = [] } = useQuery({ queryKey: ['users-light'], queryFn: async () => (await api.get('/users', { params: { pageSize: 500 } })).data });
  const userList = Array.isArray(users) ? users : (users as any)?.items ?? [];
  const emptyForm = { role: 'OPERATOR', shift: 'GENERAL', isPrimary: false };
  const [form, setForm] = useState<any>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const resetForm = () => { setForm(emptyForm); setEditingId(null); };
  const refresh = () => { qc.invalidateQueries({ queryKey: ['wms-whs-overview'] }); qc.invalidateQueries({ queryKey: ['wms-wh', warehouseId] }); };
  const add = useMutation({
    mutationFn: async () => (await api.post(`/wms/warehouses/${warehouseId}/staff`, form)).data,
    onSuccess: () => { refresh(); resetForm(); },
  });
  const update = useMutation({
    mutationFn: async () => (await api.patch(`/wms/warehouses/staff/${editingId}`, form)).data,
    onSuccess: () => { refresh(); resetForm(); },
  });
  const rm = useMutation({
    mutationFn: async (sid: number) => (await api.delete(`/wms/warehouses/staff/${sid}`)).data,
    onSuccess: () => { refresh(); if (editingId) resetForm(); },
  });
  const startEdit = (s: any) => { setForm({ userId: s.userId, role: s.role, shift: s.shift ?? 'GENERAL', isPrimary: !!s.isPrimary, notes: s.notes ?? '' }); setEditingId(s.id); };
  const saving = add.isPending || update.isPending;

  if (!w) return null;
  const activeStaff = (w.staff ?? []).filter((s: any) => s.isActive);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[820px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold flex items-center gap-2"><UsersIcon size={18} /> Staff — {w.name}</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 space-y-4">
          <div className="border border-emerald-200 bg-emerald-50 rounded p-3">
            <div className="text-sm font-semibold mb-2">{editingId ? 'Edit staff' : 'Add staff'}</div>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4"><F label="User"><select value={form.userId ?? ''} disabled={!!editingId} onChange={(e) => setForm({ ...form, userId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-500"><option value="">— pick —</option>{userList.map((u: any) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName ?? ''} — {u.userType}</option>)}</select></F></div>
              <div className="col-span-3"><F label="Role"><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">{['MANAGER','SUPERVISOR','OPERATOR','PICKER','PACKER','QC','DRIVER','GUARD','RETURNS'].map(x => <option key={x}>{x}</option>)}</select></F></div>
              <div className="col-span-2"><F label="Shift"><select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">{['GENERAL','DAY','NIGHT'].map(x => <option key={x}>{x}</option>)}</select></F></div>
              <div className="col-span-1"><F label="Primary"><input type="checkbox" checked={!!form.isPrimary} onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })} /></F></div>
              <div className="col-span-2 flex gap-1">
                {editingId
                  ? <>
                      <button onClick={() => update.mutate()} disabled={saving} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex-1">{update.isPending ? '…' : 'Update'}</button>
                      <button onClick={resetForm} disabled={saving} className="border px-2 py-1.5 rounded text-sm">Cancel</button>
                    </>
                  : <button onClick={() => add.mutate()} disabled={!form.userId || saving} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm w-full">{add.isPending ? '…' : 'Add'}</button>}
              </div>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold mb-2">Assigned staff ({activeStaff.length})</div>
            <table className="min-w-full text-sm border">
              <thead className="bg-slate-50 text-xs"><tr><th className="p-2 text-left">Name</th><th className="text-left">Mobile</th><th className="text-left">Role</th><th className="text-left">Shift</th><th>Primary</th><th /></tr></thead>
              <tbody>
                {activeStaff.length === 0 ? <tr><td colSpan={6} className="text-center py-4 text-slate-400">No staff yet</td></tr>
                  : activeStaff.map((s: any) => (
                    <tr key={s.id} className="border-t">
                      <td className="p-2">{s.user?.firstName} {s.user?.lastName ?? ''}</td>
                      <td className="text-xs">{s.user?.mobile}</td>
                      <td><span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{s.role}</span></td>
                      <td className="text-xs">{s.shift ?? '—'}</td>
                      <td className="text-center">{s.isPrimary ? '✓' : ''}</td>
                      <td className="text-right whitespace-nowrap">
                        <button onClick={() => startEdit(s)} className={`mr-2 ${editingId === s.id ? 'text-emerald-900' : 'text-emerald-700'}`} title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => rm.mutate(s.id)} className="text-red-600" title="Remove"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end"><button onClick={onClose} className="border px-4 py-1.5 rounded text-sm">Close</button></div>
      </div>
    </div>
  );
}
